import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { DataObject, Refresh, Assessment, Download, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from './constants';
import SnusProductDetailPopup from './snus/SnusProductDetailPopup';
import PublishReportControls from './snus/PublishReportControls';
import RawDataFilterBar from './snus/RawDataFilterBar';
import SwedenSnusRetailerTab from './SwedenSnusRetailerTab';

const RAW_DATASETS = [
  { id: 'dashboard', label: 'Prices' },
  { id: 'product_statuses', label: 'Availability' },
  { id: 'promos', label: 'Visibility' },
];
const RAW_PAGE_SIZES = [50, 100, 250];

function weekCsvLabel(week) {
  if (!week?.csv_on_disk) return null;
  const bits = [];
  if (week.csv_prices) bits.push(`P${week.csv_prices}`);
  if (week.csv_availability) bits.push(`A${week.csv_availability}`);
  if (week.csv_visibility) bits.push(`V${week.csv_visibility}`);
  return bits.length ? `CSV ${bits.join(' ')}` : 'CSV';
}

const apiBase = `${API_URL}/sweden-snus-admin`;

function shelfPdfFilename(parts) {
  const slug = parts
    .filter(Boolean)
    .map((part) => String(part).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))
    .filter(Boolean)
    .join('-');
  return `weekly-shelf-${slug || 'report'}.pdf`;
}

function formatCell(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function SwedenSnusAdminView() {
  const [mainTab, setMainTab] = useState('sweden');
  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DataObject />
        Sweden snus admin
      </Typography>
      <Tabs value={mainTab} onChange={(_, value) => setMainTab(value)} sx={{ mb: 2 }}>
        <Tab value="sweden" label="Sweden market" />
        <Tab value="retailer" label="Retailer (ecommerce_prod)" />
      </Tabs>
      {mainTab === 'retailer' ? <SwedenSnusRetailerTab /> : <SwedenMarketAdminPanel />}
    </Box>
  );
}

function SwedenMarketAdminPanel() {
  const [table, setTable] = useState('dashboard');
  const [preview, setPreview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [brand, setBrand] = useState('');
  const [country, setCountry] = useState('Sweden');
  const [category, setCategory] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [weeksLoading, setWeeksLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandsByCategory, setBrandsByCategory] = useState({});
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [skuRankings, setSkuRankings] = useState(null);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuPopupOpen, setSkuPopupOpen] = useState(false);
  const [skuDetail, setSkuDetail] = useState(null);
  const [skuDetailLoading, setSkuDetailLoading] = useState(false);
  const [skuDetailError, setSkuDetailError] = useState(null);
  const [selectedSkuKey, setSelectedSkuKey] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [weekDays, setWeekDays] = useState([]);
  const [loadingDay, setLoadingDay] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [rawPage, setRawPage] = useState(0);
  const [rawPageSize, setRawPageSize] = useState(100);
  const [rawQuery, setRawQuery] = useState('');
  const [rawQueryDebounced, setRawQueryDebounced] = useState('');
  const [rawBrand, setRawBrand] = useState('');
  const [rawCategory, setRawCategory] = useState('');
  const [rawRetailer, setRawRetailer] = useState('');
  const [rawDay, setRawDay] = useState('');
  const [rawWeekSeen, setRawWeekSeen] = useState(selectedWeek);
  let rawDayForScope = rawDay;
  if (rawWeekSeen !== selectedWeek) {
    setRawWeekSeen(selectedWeek);
    setRawDay('');
    rawDayForScope = '';
  }
  const rawScope = `${country}|${selectedWeek}|${table}|${rawQueryDebounced}|${rawBrand}|${rawCategory}|${rawRetailer}|${rawDayForScope}`;
  const [rawScopeSeen, setRawScopeSeen] = useState(rawScope);
  if (rawScopeSeen !== rawScope) {
    setRawScopeSeen(rawScope);
    setRawPage(0);
  }

  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/meta`, { withCredentials: true });
      setMeta(res.data);
    } catch (e) {
      setMeta(null);
    }
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { table, limit: rawPageSize, offset: rawPage * rawPageSize, country };
      if (selectedWeek) params.week = selectedWeek;
      if (rawQueryDebounced) params.q = rawQueryDebounced;
      if (rawBrand) params.brand = rawBrand;
      if (rawCategory) params.category = rawCategory;
      if (rawRetailer) params.retailer = rawRetailer;
      if (rawDayForScope) params.day = rawDayForScope;
      const res = await axios.get(`${apiBase}/datasets/preview`, {
        params,
        withCredentials: true,
      });
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      setError(e.response?.data?.detail || e.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [table, country, selectedWeek, rawPage, rawPageSize, rawQueryDebounced, rawBrand, rawCategory, rawRetailer, rawDayForScope]);

  const loadWeeks = useCallback(async (fresh = false) => {
    setWeeksLoading(true);
    try {
      const res = await axios.get(`${apiBase}/snapshots`, {
        params: { country, ...(fresh ? { fresh: true } : {}) },
        withCredentials: true,
      });
      const nextWeeks = res.data?.weeks || [];
      setWeeks(nextWeeks);
      setSelectedWeek((prev) => {
        if (prev && nextWeeks.some((w) => w.week_key === prev)) return prev;
        return nextWeeks[0]?.week_key || '';
      });
    } catch (e) {
      setWeeks([]);
      setError(e.response?.data?.detail || e.message || 'Failed to load weeks');
    } finally {
      setWeeksLoading(false);
    }
  }, [country]);

  const weekFilterReq = useRef(0);
  const loadWeekFilters = useCallback(async () => {
    const req = ++weekFilterReq.current;
    if (!selectedWeek) {
      setCategories([]);
      setBrands([]);
      setBrandsByCategory({});
      setCategory('');
      setBrand('');
      return;
    }
    setFiltersLoading(true);
    try {
      const res = await axios.get(`${apiBase}/week-filters`, {
        params: { country, week: selectedWeek },
        withCredentials: true,
      });
      if (req !== weekFilterReq.current) return;
      const nextCategories = res.data?.categories || [];
      const nextByCat = res.data?.brands_by_category || {};
      setCategories(nextCategories);
      setBrandsByCategory(nextByCat);

      setCategory((prev) => {
        if (prev && nextCategories.includes(prev)) return prev;
        return nextCategories[0] || '';
      });
    } catch (e) {
      if (req !== weekFilterReq.current) return;
      setCategories([]);
      setBrands([]);
      setBrandsByCategory({});
      setCategory('');
      setBrand('');
      setError(e.response?.data?.detail || e.message || 'Failed to load week filters');
    } finally {
      if (req === weekFilterReq.current) setFiltersLoading(false);
    }
  }, [country, selectedWeek]);

  // Keep brand list in sync with selected category (from weekly dataset)
  useEffect(() => {
    const nextBrands = category
      ? brandsByCategory[category] || []
      : Object.values(brandsByCategory).flat().filter((v, i, a) => a.indexOf(v) === i);
    setBrands(nextBrands);
    setBrand((prev) => {
      if (prev && nextBrands.includes(prev)) return prev;
      return nextBrands[0] || '';
    });
  }, [category, brandsByCategory]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setSkuLoading(true);
    setError(null);
    const params = { brand, country, category };
    if (selectedWeek) params.week = selectedWeek;
    try {
      const [oppRes, skuRes] = await Promise.all([
        axios.get(`${apiBase}/brand-opportunity`, { params, withCredentials: true }),
        axios.get(`${apiBase}/sku-rankings`, { params: { ...params, limit: 30 }, withCredentials: true }),
      ]);
      setHealth(oppRes.data);
      setSkuRankings(skuRes.data);
    } catch (e) {
      setHealth(null);
      setSkuRankings(null);
      setError(e.response?.data?.detail || e.message || 'Intelligence lookup failed');
    } finally {
      setHealthLoading(false);
      setSkuLoading(false);
    }
  }, [brand, country, category, selectedWeek]);

  const openSkuDetail = useCallback(
    async (sku) => {
      if (!sku?.sku_key) return;
      setSelectedSkuKey(sku.sku_key);
      setSkuPopupOpen(true);
      setSkuDetail(null);
      setSkuDetailError(null);
      setSkuDetailLoading(true);
      try {
        const params = {
          sku_key: sku.sku_key,
          brand,
          country,
          category,
        };
        if (selectedWeek) params.week = selectedWeek;
        const res = await axios.get(`${apiBase}/sku-detail`, {
          params,
          withCredentials: true,
        });
        setSkuDetail(res.data);
      } catch (e) {
        setSkuDetail(null);
        setSkuDetailError(e.response?.data?.detail || e.message || 'Failed to load product detail');
      } finally {
        setSkuDetailLoading(false);
      }
    },
    [brand, country, category, selectedWeek]
  );

  const loadWeekDays = useCallback(async () => {
    if (!selectedWeek) {
      setWeekDays([]);
      return;
    }
    try {
      const res = await axios.get(`${apiBase}/week-days`, {
        params: { country, week: selectedWeek },
        withCredentials: true,
      });
      setWeekDays(res.data?.days || []);
    } catch (e) {
      setWeekDays([]);
    }
  }, [country, selectedWeek]);

  const refreshDailyCsv = useCallback(
    async (tables) => {
      if (!selectedWeek) return;
      setRefreshing(true);
      setRefreshMsg(null);
      setError(null);
      try {
        const body = { week: selectedWeek, country, force: false };
        if (tables?.length) body.tables = tables;
        const res = await axios.post(`${apiBase}/refresh-data`, body, {
          withCredentials: true,
          headers: { 'content-type': 'application/json' },
        });
        const label = tables?.length ? tables.join(', ') : 'all tables';
        setRefreshMsg(res.data?.message || `Week CSVs refreshed (${label})`);
        await loadMeta();
        await loadWeeks();
        await loadWeekDays();
        await loadWeekFilters();
      } catch (e) {
        setError(e.response?.data?.detail || e.message || 'Failed to refresh week CSVs');
      } finally {
        setRefreshing(false);
      }
    },
    [selectedWeek, country, loadMeta, loadWeeks, loadWeekDays, loadWeekFilters]
  );

  const loadSingleDay = useCallback(
    async (dateStr, { alreadyCached = false, emptyFile = false, tables = ['dashboard'] } = {}) => {
      if (!dateStr) return;
      setLoadingDay(dateStr);
      setRefreshMsg(null);
      setError(null);
      try {
        if (!alreadyCached) {
          const res = await axios.post(
            `${apiBase}/refresh-data`,
            { date: dateStr, country, force: !!emptyFile, tables },
            { withCredentials: true, headers: { 'content-type': 'application/json' } }
          );
          setRefreshMsg(res.data?.message || `Loaded ${dateStr}`);
          await loadMeta();
          await loadWeekDays();
        } else {
          setRefreshMsg(`Using cached ${dateStr}`);
        }
        await loadWeekFilters();
        await loadPreview();
      } catch (e) {
        setError(e.response?.data?.detail || e.message || `Failed to load ${dateStr}`);
      } finally {
        setLoadingDay(null);
      }
    },
    [country, loadMeta, loadWeekDays, loadWeekFilters, loadPreview]
  );

  const exportWeeklyPdf = useCallback(async () => {
    if (!selectedWeek) return;
    setExportingPdf(true);
    setError(null);
    try {
      const params = { country, week: selectedWeek };
      if (brand) params.brand = brand;
      if (category) params.category = category;
      const res = await axios.get(`${apiBase}/weekly-export.pdf`, {
        params,
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.href = url;
      link.download = match?.[1] || shelfPdfFilename([country, selectedWeek, category, brand]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let message = e.message || 'Failed to export weekly PDF';
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          message = JSON.parse(text).detail || text || message;
        } catch {
          message = 'Failed to export weekly PDF';
        }
      } else if (e.response?.data?.detail) {
        message = e.response.data.detail;
      }
      setError(typeof message === 'string' ? message : 'Failed to export weekly PDF');
    } finally {
      setExportingPdf(false);
    }
  }, [selectedWeek, country, brand, category]);

  function formatWow(wow) {
    if (wow == null || Number.isNaN(wow)) return '—';
    const n = Number(wow);
    if (n > 0) return `↑ ${Math.abs(n)} pts WoW`;
    if (n < 0) return `↓ ${Math.abs(n)} pts WoW`;
    return '→ flat WoW';
  }

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    const timer = setTimeout(() => setRawQueryDebounced(rawQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  const clearRawFilters = () => {
    setRawQuery('');
    setRawQueryDebounced('');
    setRawBrand('');
    setRawCategory('');
    setRawRetailer('');
    setRawDay('');
  };

  useEffect(() => {
    const timer = setTimeout(() => loadWeeks(), 300);
    return () => clearTimeout(timer);
  }, [loadWeeks]);

  useEffect(() => {
    if (!selectedWeek) {
      setWeekDays([]);
      setPreview(null);
      setCategories([]);
      setBrands([]);
      setBrandsByCategory({});
      setCategory('');
      setBrand('');
      setHealth(null);
      setSkuRankings(null);
      return;
    }
    setHealth(null);
    setSkuRankings(null);
    const timer = setTimeout(() => {
      loadWeekDays();
      loadWeekFilters();
    }, 100);
    return () => clearTimeout(timer);
  }, [loadWeekDays, loadWeekFilters, selectedWeek]);

  useEffect(() => {
    if (!showRaw || !selectedWeek) return undefined;
    const timer = setTimeout(() => loadPreview(), 150);
    return () => clearTimeout(timer);
  }, [showRaw, loadPreview, selectedWeek]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pick a <strong>week</strong>, then download datasets separately:{' '}
        <strong>prices</strong> (dashboard), <strong>availability</strong> (product_statuses),{' '}
        <strong>visibility</strong> (promos). Intelligence only scores dimensions for datasets on disk.
      </Typography>

      {meta && !meta.config_present && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          MySQL config missing on server ({meta.config_path}). Check{' '}
          <code>database/bat/config-mysql.ini</code>.
        </Alert>
      )}

      {meta?.daily_cache && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Daily CSV cache: {meta.daily_cache.cached_day_count ?? 0} day(s)
          {meta.daily_cache.latest_date ? ` · latest ${meta.daily_cache.latest_date}` : ''}
          {meta.daily_cache.dir ? (
            <>
              {' '}
              under <code>{meta.daily_cache.dir}</code>
            </>
          ) : null}
        </Alert>
      )}

      {refreshMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRefreshMsg(null)}>
          {refreshMsg}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: weekDays.length ? 2 : 0 }}>
            <TextField
              size="small"
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 420 }}>
              <InputLabel>Week</InputLabel>
              <Select
                label="Week"
                value={weeks.some((w) => w.week_key === selectedWeek) ? selectedWeek : ''}
                onChange={(e) => setSelectedWeek(e.target.value)}
                disabled={weeksLoading || weeks.length === 0}
                displayEmpty
              >
                {weeks.length === 0 && (
                  <MenuItem value="" disabled>
                    {weeksLoading ? 'Loading weeks…' : 'No weeks found'}
                  </MenuItem>
                )}
                {weeks.map((w) => {
                  const csvLabel = weekCsvLabel(w);
                  return (
                    <MenuItem key={w.week_key} value={w.week_key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <span>
                          {w.week_label}
                          {w.scrape_days
                            ? ` · ${w.scrape_days} scrape day${w.scrape_days === 1 ? '' : 's'}`
                            : ''}
                        </span>
                        {csvLabel ? (
                          <Chip size="small" color="success" label={csvLabel} sx={{ height: 22 }} />
                        ) : w.in_dataset ? (
                          <Chip size="small" variant="outlined" label="Not on disk" sx={{ height: 22 }} />
                        ) : null}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Button
              startIcon={<Refresh />}
              onClick={() => {
                loadWeeks(true);
                loadWeekFilters();
              }}
              disabled={weeksLoading}
            >
              Reload weeks
            </Button>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <Download />}
              onClick={() => refreshDailyCsv(['dashboard'])}
              disabled={refreshing || !selectedWeek || !!loadingDay}
            >
              Prices
            </Button>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <Download />}
              onClick={() => refreshDailyCsv(['product_statuses'])}
              disabled={refreshing || !selectedWeek || !!loadingDay}
            >
              Availability
            </Button>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <Download />}
              onClick={() => refreshDailyCsv(['promos'])}
              disabled={refreshing || !selectedWeek || !!loadingDay}
            >
              Visibility
            </Button>
            {(weeksLoading || refreshing) && <CircularProgress size={20} />}
          </Box>

          {selectedWeek && weekDays.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Days in {selectedWeek}. Green means prices are already a local CSV. Outlined days are in the
                dataset and not downloaded yet. P/A/V = prices / availability / visibility on disk.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {weekDays.map((d) => {
                  const busy = loadingDay === d.date;
                  const chipColor = d.cached ? 'success' : d.empty_file ? 'warning' : 'default';
                  const chipVariant = d.cached || d.empty_file ? 'filled' : 'outlined';
                  const notInDataset = d.has_scrape === false;
                  const badges = [
                    d.has_dashboard || d.cached ? 'P' : '',
                    d.has_product_statuses ? 'A' : '',
                    d.has_promos ? 'V' : '',
                  ]
                    .filter(Boolean)
                    .join('');
                  return (
                    <Chip
                      key={d.date}
                      clickable={!busy && !refreshing}
                      disabled={busy || refreshing}
                      color={chipColor}
                      variant={chipVariant}
                      icon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
                      label={`${d.weekday} ${d.date.slice(5)}${badges ? ` · ${badges}` : ''}${notInDataset ? '' : d.has_scrape && !d.cached && !d.empty_file ? ' · in data' : ''}`}
                      sx={notInDataset ? { opacity: 0.45 } : undefined}
                      onClick={() => {
                        if (!busy && !refreshing) {
                          loadSingleDay(d.date, {
                            alreadyCached: !!d.cached,
                            emptyFile: !!d.empty_file,
                            tables: ['dashboard'],
                          });
                        }
                      }}
                      title={
                        [
                          d.has_scrape === true
                            ? 'in the dataset'
                            : d.has_scrape === false
                              ? 'no scrape this day'
                              : '',
                          d.cached ? 'prices CSV on disk' : d.empty_file ? 'prices CSV empty' : 'prices not downloaded',
                          d.has_product_statuses ? 'availability on disk' : 'availability not downloaded',
                          d.has_promos ? 'visibility on disk' : 'visibility not downloaded',
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      }
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assessment fontSize="small" />
                Brand health
              </Typography>
              <Autocomplete
                options={categories}
                value={categories.includes(category) ? category : null}
                onChange={(_, value) => setCategory(value || '')}
                loading={filtersLoading}
                disabled={filtersLoading || categories.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    size="small"
                    placeholder={filtersLoading ? 'Loading…' : 'Select category'}
                  />
                )}
                sx={{ mb: 1.5 }}
              />
              <Autocomplete
                options={brands}
                value={brands.includes(brand) ? brand : null}
                onChange={(_, value) => setBrand(value || '')}
                loading={filtersLoading}
                disabled={filtersLoading || brands.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand"
                    size="small"
                    placeholder={filtersLoading ? 'Loading…' : 'Select brand'}
                  />
                )}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="contained"
                  disabled={healthLoading || !selectedWeek || !brand || !category}
                  onClick={loadHealth}
                >
                  {healthLoading ? 'Loading…' : 'Run weekly intelligence'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={exportingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
                  disabled={exportingPdf || !selectedWeek}
                  onClick={exportWeeklyPdf}
                >
                  {exportingPdf ? 'Preparing PDF…' : 'Customer weekly PDF'}
                </Button>
                <PublishReportControls
                  disabled={!selectedWeek}
                  payload={{
                    country,
                    week: selectedWeek,
                    brand: brand || null,
                    category: category || null,
                  }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                PDF and the published page compare this week with the previous week: prices, assortment, visibility, and stock.
                {brand && category ? ` Scoped to ${brand} · ${category}.` : ' Leave brand and category empty for the whole market.'}
              </Typography>
              {filtersLoading && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Inferring brands/categories from weekly dataset…
                </Typography>
              )}
              {health && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    {health.country} · {health.category}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {health.brand}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Digital shelf health (from {health.sku_count ?? '—'} SKUs)
                  </Typography>
                  {health.datasets && (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                      {[
                        ['dashboard', 'Prices'],
                        ['product_statuses', 'Availability'],
                        ['promos', 'Visibility'],
                      ].map(([key, label]) => (
                        <Chip
                          key={key}
                          size="small"
                          label={label}
                          color={health.datasets[key] ? 'success' : 'default'}
                          variant={health.datasets[key] ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Stack>
                  )}
                  {health.active_dimensions?.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Scoring: {health.active_dimensions.join(', ')}
                    </Typography>
                  )}
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {health.health_score ?? health.score}/100
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    Rank #{health.rank} / {health.total_brands}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: health.wow_change < 0 ? 'error.main' : 'success.main', mb: 1.5 }}
                  >
                    {formatWow(health.wow_change)}
                  </Typography>
                  {health.skus_with_issues > 0 && (
                    <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                      {health.skus_with_issues} SKU{health.skus_with_issues !== 1 ? 's' : ''} with significant
                      shelf issues
                      {health.sku_risk && (
                        <>
                          {' '}
                          (🔴 {health.sku_risk.critical ?? 0} · 🟠 {health.sku_risk.high ?? 0} · 🟡{' '}
                          {health.sku_risk.moderate ?? 0})
                        </>
                      )}
                    </Alert>
                  )}
                  {health.top_issues?.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Why the brand score (aggregated)
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {health.top_issues.map((issue) => (
                          <Typography component="li" variant="body2" key={issue}>
                            {issue}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    vs {health.competitor} ({health.competitor_score}) ·{' '}
                    {health.week || selectedWeek || 'week'} · scrape {health.snapshot_date}
                  </Typography>
                </Paper>
              )}
              {skuRankings?.skus?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    SKU health (child of brand · mean = {skuRankings.brand_health_score ?? '—'}/100)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>SKU</TableCell>
                          <TableCell align="right">Health</TableCell>
                          <TableCell>Main issue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {skuRankings.skus.map((sku, i) => (
                          <TableRow
                            key={sku.sku_key || i}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => openSkuDetail(sku)}
                          >
                            <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {sku.title}
                            </TableCell>
                            <TableCell align="right">{sku.health_score}</TableCell>
                            <TableCell>{sku.main_issue_display ?? sku.main_issue ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Click a SKU for product detail (pricing, availability, visibility by retailer).
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6">Raw data</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showRaw}
                  onChange={(e) => setShowRaw(e.target.checked)}
                  disabled={!selectedWeek}
                />
              }
              label="Show downloaded rows"
            />
            {showRaw && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Dataset</InputLabel>
                <Select label="Dataset" value={table} onChange={(e) => setTable(e.target.value)}>
                  {RAW_DATASETS.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {showRaw && (
              <Button startIcon={<Refresh />} onClick={loadPreview} disabled={loading || !selectedWeek}>
                Refresh
              </Button>
            )}
            {showRaw && preview && (
              <Typography variant="body2" color="text.secondary">
                {preview.estimated_total_rows != null
                  ? `${preview.estimated_total_rows.toLocaleString()} rows in week`
                  : `${preview.returned} rows`}
                {preview.week_start && preview.week_end
                  ? ` · ${preview.week_start} → ${preview.week_end}`
                  : ''}
                {preview.scrape_days != null ? ` · ${preview.scrape_days} scrape days` : ''}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Prices, availability, and visibility rows already downloaded for this week. Filters apply before paging.
          </Typography>
          {showRaw && (
            <RawDataFilterBar
              query={rawQuery}
              onQueryChange={setRawQuery}
              brand={rawBrand}
              category={rawCategory}
              retailer={rawRetailer}
              day={rawDay}
              onBrandChange={setRawBrand}
              onCategoryChange={setRawCategory}
              onRetailerChange={setRawRetailer}
              onDayChange={setRawDay}
              facets={preview?.facets}
              onClear={clearRawFilters}
            />
          )}

          {showRaw && loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {showRaw && !loading && preview && (preview.estimated_total_rows > 0 || preview.rows?.length > 0) && (
            <TablePagination
              component="div"
              count={preview.estimated_total_rows ?? preview.rows.length}
              page={rawPage}
              onPageChange={(_, nextPage) => setRawPage(nextPage)}
              rowsPerPage={rawPageSize}
              onRowsPerPageChange={(event) => {
                setRawPageSize(Number(event.target.value));
                setRawPage(0);
              }}
              rowsPerPageOptions={RAW_PAGE_SIZES}
            />
          )}

          {showRaw && !loading && preview && preview.rows?.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {(preview.columns || []).map((col) => (
                      <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row, i) => (
                    <TableRow key={i}>
                      {(preview.columns || []).map((col) => (
                        <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                          {formatCell(row[col])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {showRaw && !loading && preview && !(preview.estimated_total_rows > 0) && !preview.rows?.length && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              No rows in this dataset for the selected week. Download it with the buttons above first.
            </Typography>
          )}
        </CardContent>
      </Card>

      <SnusProductDetailPopup
        open={skuPopupOpen}
        onClose={() => setSkuPopupOpen(false)}
        detail={skuDetail}
        loading={skuDetailLoading}
        error={skuDetailError}
        skuKey={selectedSkuKey}
        country={country}
        brand={brand}
        category={category}
        week={selectedWeek}
      />
    </Box>
  );
}
